import { useState, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Trash2 } from "lucide-react";
import { PencilIcon, PlusIcon } from "@/components/developer/DashboardIcons";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Json } from "@/integrations/supabase/types";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

interface Skill {
  skill_name: string;
}

const WorkExperience = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentlyWorkHere, setCurrentlyWorkHere] = useState(false);
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [startDate, setStartDate] = useState({ year: "", month: "" });
  const [endDate, setEndDate] = useState({ year: "", month: "" });
  const [description, setDescription] = useState("");
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>([]);
  const [currentSkill, setCurrentSkill] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentExperienceId, setCurrentExperienceId] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    title?: string;
    company?: string;
    startDate?: string;
    endDate?: string;
    description?: string;
  }>({});
  const { toast } = useToast();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const years = Array.from({ length: 30 }, (_, i) => 2024 - i);
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const { data: workExperiences = [], isFetching, refetch } = useQuery({
    queryKey: ['work-experience'],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('work_experience')
        .select('*')
        .eq('user_id', user.user.id)
        .order('start_date', { ascending: false });

      if (error) throw error;
      return data.map(el=>({
        ...el,
        skills: el.skills.map(skill=>({skill_name: skill}))
      }));
    }
  });

  const { data: skillTerms = [], isFetching: isTermsLoading } = useQuery({
    queryKey: ["skillTerms"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("skill_terms")
        .select("*")
        .order("term");
      
      if (error) throw error;
      return data;
    },
  });

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!title.trim()) {
      newErrors.title = "Job title is required";
    }
    if (!company.trim()) {
      newErrors.company = "Company is required";
    }
    if (!startDate.year || !startDate.month) {
      newErrors.startDate = "Start date is required";
    }
    if (!currentlyWorkHere && (!endDate.year || !endDate.month)) {
      newErrors.endDate = "End date is required";
    }
    if (!description.trim()) {
      newErrors.description = "Description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setTitle("");
    setCompany("");
    setStartDate({ year: "", month: "" });
    setEndDate({ year: "", month: "" });
    setDescription("");
    setSelectedSkills([]);
    setCurrentlyWorkHere(false);
    setErrors({});
    setCurrentExperienceId(null);
  };

  const handleAddSkill = () => {
    if (!currentSkill) return;
    
    // Check if skill already exists in selectedSkills
    const skillExists = selectedSkills.some(
      skill => skill.skill_name.toLowerCase() === currentSkill.toLowerCase()
    );

    if (skillExists) {
      toast({
        title: "Duplicate Skill",
        description: "This skill has already been added",
        variant: "destructive",
      });
      return;
    }

    setSelectedSkills([...selectedSkills, { 
      skill_name: currentSkill
    }]);
    setCurrentSkill("");
    setShowSuggestions(false);
  };

  const handleAddNew = () => {
    resetForm();
    setIsEditing(true);
  };

  const handleDelete = async () => {
    if (!currentExperienceId) return;

    try {
      const { error } = await supabase
        .from('work_experience')
        .delete()
        .eq('id', currentExperienceId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Work experience deleted successfully.",
      });

      setIsEditing(false);
      resetForm();
      await refetch();
    } catch (error) {
      console.error('Error deleting work experience:', error);
      toast({
        title: "Error",
        description: "Failed to delete work experience. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('No user found');

      const startDateObj = new Date(`${startDate.year}-${months.indexOf(startDate.month) + 1}-01`);
      let endDateObj = null;
      
      if (!currentlyWorkHere && endDate.year && endDate.month) {
        endDateObj = new Date(`${endDate.year}-${months.indexOf(endDate.month) + 1}-01`);
      }

      // Convert selected skills to string array
      const skillsArray = selectedSkills.map(skill => skill.skill_name);

      const experienceData = {
        user_id: user.data.user.id,
        title,
        company,
        start_date: startDateObj.toISOString(),
        end_date: endDateObj?.toISOString() || null,
        responsibilities: description,
        skills: skillsArray,
      };

      const { error } = currentExperienceId
        ? await supabase
            .from('work_experience')
            .update(experienceData)
            .eq('id', currentExperienceId)
        : await supabase
            .from('work_experience')
            .insert(experienceData);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Work experience ${currentExperienceId ? 'updated' : 'added'} successfully.`,
      });

      setIsEditing(false);
      await refetch();
    } catch (error) {
      console.error('Error saving work experience:', error);
      toast({
        title: "Error",
        description: "Failed to save work experience. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    resetForm();
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    setSelectedSkills(selectedSkills.filter(skill => skill.skill_name !== skillToRemove));
  };

  if (isFetching) {
    return (
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Work experience</h2>
        </div>
        <div className="animate-pulse">
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </Card>
    );
  }

  if (isEditing) {
    return (
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Work experience</h2>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company">Company *</Label>
              <Input 
                id="company" 
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className={errors.company ? "border-red-500" : ""}
              />
              {errors.company && (
                <p className="text-sm text-red-500">{errors.company}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="jobTitle">Job title *</Label>
              <Input 
                id="jobTitle" 
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={errors.title ? "border-red-500" : ""}
              />
              {errors.title && (
                <p className="text-sm text-red-500">{errors.title}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Technical skills used *</Label>
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <Input
                  ref={inputRef}
                  placeholder="Add a skill"
                  value={currentSkill}
                  onChange={(e) => {
                    setCurrentSkill(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                />
                {showSuggestions && (
                  <div ref={dropdownRef} className="absolute w-full z-10">
                    <Command className="rounded-lg border shadow-md">
                      <CommandList>
                        <CommandEmpty>No skill found.</CommandEmpty>
                        <CommandGroup className="max-h-[200px] overflow-auto">
                          {skillTerms
                            .filter(term => 
                              term.term.toLowerCase().includes(currentSkill.toLowerCase())
                            )
                            .map((term) => (
                              <CommandItem
                                key={term.id}
                                value={term.term}
                                onSelect={(value) => {
                                  setCurrentSkill(value);
                                  setShowSuggestions(false);
                                }}
                                className="cursor-pointer"
                              >
                                {term.term}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddSkill}
                className="whitespace-nowrap"
              >
                + Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedSkills.map((skill) => (
                <Badge
                  key={skill.skill_name}
                  variant="secondary"
                  className="flex items-center gap-1 pr-2 py-2"
                >
                  {skill.skill_name}
                  <button
                    onClick={() => handleRemoveSkill(skill.skill_name)}
                    className="ml-1 text-xs hover:text-destructive"
                  >
                    X
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>From *</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select 
                  value={startDate.year}
                  onValueChange={(value) => setStartDate(prev => ({ ...prev, year: value }))}
                >
                  <SelectTrigger className={errors.startDate ? "border-red-500" : ""}>
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={startDate.month}
                  onValueChange={(value) => setStartDate(prev => ({ ...prev, month: value }))}
                >
                  <SelectTrigger className={errors.startDate ? "border-red-500" : ""}>
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month) => (
                      <SelectItem key={month} value={month}>
                        {month}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {errors.startDate && (
                <p className="text-sm text-red-500">{errors.startDate}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>To *</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select 
                  disabled={currentlyWorkHere}
                  value={endDate.year}
                  onValueChange={(value) => setEndDate(prev => ({ ...prev, year: value }))}
                >
                  <SelectTrigger className={errors.endDate ? "border-red-500" : ""}>
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  disabled={currentlyWorkHere}
                  value={endDate.month}
                  onValueChange={(value) => setEndDate(prev => ({ ...prev, month: value }))}
                >
                  <SelectTrigger className={errors.endDate ? "border-red-500" : ""}>
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month) => (
                      <SelectItem key={month} value={month}>
                        {month}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {errors.endDate && (
                <p className="text-sm text-red-500">{errors.endDate}</p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="currentlyWork"
              checked={currentlyWorkHere}
              onCheckedChange={(checked) => {
                setCurrentlyWorkHere(checked as boolean);
                if (checked) {
                  setErrors(prev => ({ ...prev, endDate: undefined }));
                }
              }}
            />
            <Label htmlFor="currentlyWork">I currently work here</Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Achievements *</Label>
            <Textarea
              id="description"
              className={`min-h-[100px] ${errors.description ? "border-red-500" : ""}`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            {errors.description && (
              <p className="text-sm text-red-500">{errors.description}</p>
            )}
          </div>

          <div className="flex justify-between">
            <div>
              <Button
                variant="destructive"
                onClick={handleDelete}
                className={cn("gap-2", !currentExperienceId && 'hidden')}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </div>
            <div className="space-x-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (workExperiences.length === 0) {
    return (
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Work experience</h2>
        </div>
        <div className="text-center py-4">
          <p className="text-gray-500 mb-4">No work experience added yet</p>
          <Button 
            onClick={handleAddNew}
            className="gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Add Work Experience
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Work experience</h2>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleAddNew}
          className="gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          Add
        </Button>
      </div>
      <div className="space-y-6">
        {workExperiences.map((experience) => (
          <div key={experience.id} className="group relative">
            <div className="flex justify-between mb-2">
              <div>
                <h3 className="font-semibold">{experience.title}</h3>
                <p className="text-sm text-gray-600">{experience.company}</p>
              </div>
              <div className="flex items-start gap-2">
                <p className="text-sm text-gray-600">
                  {experience.start_date?(
                    `${
                      new Date(experience.start_date).toLocaleDateString('en-US', { 
                        month: 'long',
                        year: 'numeric'
                      })} - ${
                        experience.end_date 
                          ? new Date(experience.end_date).toLocaleDateString('en-US', {
                              month: 'long',
                              year: 'numeric'
                            })
                          : "Present"
                      }`
                  ):("Please add start and end dates")}
                </p>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setTitle(experience.title);
                    setCompany(experience.company);
                    const startDateObj = new Date(experience.start_date);
                    setStartDate({
                      year: startDateObj.getFullYear().toString(),
                      month: months[startDateObj.getMonth()]
                    });
                    if (experience.end_date) {
                      const endDateObj = new Date(experience.end_date);
                      setEndDate({
                        year: endDateObj.getFullYear().toString(),
                        month: months[endDateObj.getMonth()]
                      });
                      setCurrentlyWorkHere(false);
                    } else {
                      setCurrentlyWorkHere(true);
                    }
                    setDescription(experience.responsibilities || "");
                    setSelectedSkills((experience.skills as unknown as Skill[]) || []);
                    setCurrentExperienceId(experience.id);
                    setIsEditing(true);
                  }}
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {((experience.skills as unknown as Skill[]) || []).map((skill) => (
                <Badge key={skill.skill_name} variant="secondary">
                  {skill.skill_name}
                </Badge>
              ))}
            </div>
            <p className="text-sm text-gray-600" style={{ whiteSpace: "pre-line" }}>{experience.responsibilities}</p>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default WorkExperience;
